import React, { Fragment } from 'react';
import { Plus, Minus, Search, MoreHorizontal, Check } from 'lucide-react';
import { Popover, Transition } from '@headlessui/react';
import { FaFilter } from 'react-icons/fa';

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'createdDate', direction: 'DESC' },
  { label: 'Oldest First', value: 'createdDate', direction: 'ASC' },
  { label: 'Priority: High to Low', value: 'workPriority', direction: 'DESC' },
  { label: 'Priority: Low to High', value: 'workPriority', direction: 'ASC' },
  { label: 'Due Date', value: 'dueDate', direction: 'ASC' },
];

interface ChildItemsHeaderProps {
  titleText: string;
  isSubtaskMode: boolean;
  expandedMode: 'create' | 'link' | null;
  onToggleCreate: () => void;
  onToggleLink: () => void;
  isFilterOpen: boolean;
  onToggleFilter: () => void;
  hasActiveFilters: boolean;
  getActiveFilterCount: () => number;
  activeFilters: any;
  setActiveFilters: (fn: any) => void;
  /** Hides creating and choosing children; the filter stays. */
  readOnly?: boolean;
}

const ChildItemsHeader: React.FC<ChildItemsHeaderProps> = ({
  titleText,
  isSubtaskMode,
  expandedMode,
  onToggleCreate,
  onToggleLink,
  isFilterOpen,
  onToggleFilter,
  hasActiveFilters,
  getActiveFilterCount,
  activeFilters,
  setActiveFilters,
  readOnly = false,
}) => (
  <div className="flex items-center justify-between gap-3 mb-3">
    {/* Title + expand create toggle */}
    <div
      className={`flex items-center gap-2 ${readOnly ? '' : 'cursor-pointer hover:opacity-80 transition-opacity'}`}
      onClick={readOnly ? undefined : onToggleCreate}
    >
      {!readOnly && (expandedMode === 'create'
        ? <Minus className="w-4 h-4 text-purple-500" />
        : <Plus className="w-4 h-4 text-purple-500" />
      )}
      <h3 className="font-medium text-neutral text-sm">{titleText}</h3>
    </div>

    {/* Right-side action buttons */}
    <div className="flex items-center gap-2 relative">
      {/* Choose existing (non-subtask only) */}
      {!isSubtaskMode && !readOnly && (
        <button
          onClick={onToggleLink}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors min-h-[36px] ${
            expandedMode === 'link'
              ? 'bg-primary-subtle border-primary-subtle text-neutral'
              : 'bg-primary-variant border-primary-subtle text-neutral-medium hover:bg-primary-subtle'
          }`}
        >
          <Search className="w-3.5 h-3.5" />
          <span>Choose existing</span>
        </button>
      )}

      {/* Filter button + badge */}
      <button
        onClick={onToggleFilter}
        className="flex items-center gap-2 px-3 py-1.5 bg-primary-variant border border-primary-subtle rounded-lg text-neutral-medium hover:bg-primary-subtle transition-colors min-h-[36px]"
      >
        <FaFilter size={12} />
        <span className="text-sm">Filter</span>
      </button>
      {hasActiveFilters && (
        <span className="absolute top-0 right-[44px] bg-blue-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold border-2 border-primary-variant">
          {getActiveFilterCount()}
        </span>
      )}

      {/* Sort / View Options popover */}
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              className={`
                relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors border
                ${open
                  ? 'bg-primary text-white border-primary-subtle'
                  : 'bg-primary-variant text-neutral-medium hover:text-white border-primary-subtle hover:bg-primary-subtle'
                }
              `}
            >
              <MoreHorizontal size={18} />
              {activeFilters.hideCompleted && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-primary-variant shadow-sm" />
              )}
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                anchor="bottom end"
                className="z-[100] w-64 rounded-xl bg-primary-variant border border-primary-subtle p-4 shadow-2xl focus:outline-none [--anchor-gap:8px]"
              >
                <div className="flex flex-col gap-4">
                  <span className="text-[10px] font-bold text-neutral-medium tracking-wider uppercase">
                    View Option
                  </span>

                  {/* Hide Done toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white/90">Hide done work items</span>
                    <button
                      onClick={() => setActiveFilters((prev: any) => ({ ...prev, hideCompleted: !prev.hideCompleted }))}
                      className={`
                        relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                        transition-colors duration-200 ease-in-out focus:outline-none
                        ${activeFilters.hideCompleted ? 'bg-blue-600' : 'bg-white/10'}
                      `}
                    >
                      <span
                        aria-hidden="true"
                        className={`
                          pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                          transition duration-200 ease-in-out
                          ${activeFilters.hideCompleted ? 'translate-x-4' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Sort By */}
                  <div className="border-t border-primary-subtle pt-4 mt-2">
                    <span className="text-[10px] font-bold text-neutral-medium tracking-wider uppercase mb-3 block">
                      Sort By
                    </span>
                    <div className="flex flex-col gap-1">
                      {SORT_OPTIONS.map(opt => (
                        <button
                          key={`${opt.label}-${opt.value}`}
                          onClick={() => setActiveFilters((prev: any) => ({ ...prev, sortBy: opt.value, sortDirection: opt.direction }))}
                          className={`flex items-center justify-between px-2 py-2 rounded-lg text-sm transition-colors ${
                            activeFilters.sortBy === opt.value && activeFilters.sortDirection === opt.direction
                              ? 'bg-blue-500/10 text-blue-400'
                              : 'text-neutral-medium hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{opt.label}</span>
                          {activeFilters.sortBy === opt.value && activeFilters.sortDirection === opt.direction && (
                            <Check size={14} />
                          )}
                        </button>
                      ))}

                      {(activeFilters.sortBy !== 'listOrder' || activeFilters.sortDirection !== 'ASC') && (
                        <button
                          onClick={() => setActiveFilters((prev: any) => ({ ...prev, sortBy: 'listOrder', sortDirection: 'ASC' }))}
                          className="mt-2 text-xs font-semibold text-red-400 hover:text-red-300 transition-colors self-start px-2 py-1 bg-red-500/10 rounded-md hover:bg-red-500/20"
                        >
                          Clear Sort
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  </div>
);

export default ChildItemsHeader;
